import React, { useState, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import {
    useGetUniversityCourseSettingsQuery,
    useSaveUniversityCourseSettingsMutation
} from "../../store/services/university-service";
import {
    Alert,
    Box,
    Button,
    Grid,
    IconButton,
    Snackbar,
    Tooltip,
    Typography,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    DialogContentText
} from "@mui/material";
import MainCard from "../../components/cards/MainCard";
import { useNavigate } from "react-router-dom";
import HelpIcon from "@mui/icons-material/HelpOutline";
import { APP_URL } from "../../config";
import { useTheme } from "@mui/material/styles";
import { getCardColor } from "../../utils/colorUtils";
import ReportedSimEmailPreview from "../../components/EmailPreviews/ReportedSimEmailPreview";
import ReportedSimEmailPreviewModal from "../../components/EmailPreviews/ReportedSimEmailPreviewModal";
import LoadingSwitch from "../../components/LoadingSwitch";
import { isCustomReportedSimEmailConfigured } from "../../utils/custom-email-utils/customEmailValidation";

ReportedSimEmailSettings.propTypes = {
    data: PropTypes.object
};

export default function ReportedSimEmailSettings(props) {
    const { data } = props;
    const navigate = useNavigate();
    const theme = useTheme();
    const lightMode = theme.palette.mode === 'light';

    const [tenancyData, setTenancyData] = useState(data);
    const [useCustomTemplate, setUseCustomTemplate] = useState(false);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [pendingValue, setPendingValue] = useState(null);
    const [originalCustomTemplate, setOriginalCustomTemplate] = useState(null);
    const [previewContent, setPreviewContent] = useState('');

    // State for the preview modal
    const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);

    // Get email colors from localStorage or use defaults
    const emailColors = {
        primary: tenancyData?.primaryHex || '#74E1BF',
        secondary: tenancyData?.secondaryHex || '#4281EE',
        tertiary: tenancyData?.tertiaryHex || '#FDC95D'
    };

    const {
        data: universitySettings,
        error: universitySettingsError,
        isLoading: universitySettingsLoading
    } = useGetUniversityCourseSettingsQuery({ tenancyId: tenancyData.tenancyId });

    const [updateUniversityCourseSettings] = useSaveUniversityCourseSettingsMutation();
    const {
        data: defaultTemplateSettings,
        isLoading: defaultTemplateLoading
    } = useGetUniversityCourseSettingsQuery({ tenancyId: 0 });

    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarSeverity, setSnackbarSeverity] = useState("success");

    useEffect(() => {
        if (universitySettings) {
            const hasReportedSimConfig = isCustomReportedSimEmailConfigured(universitySettings);

            const hasCustomReportedSimTemplate = hasReportedSimConfig && !universitySettings.useDefaultReportedSimTemplate;
            setUseCustomTemplate(hasCustomReportedSimTemplate);
                
            // Only store original custom template if reported sim fields are actually configured
            if (hasCustomReportedSimTemplate) {
                setOriginalCustomTemplate({
                    reportedSimBodyText: universitySettings.reportedSimBodyText,
                    reportedSimMessage: universitySettings.reportedSimMessage,
                    reportedSimEmailSubject: universitySettings.reportedSimEmailSubject
                });
            }
        }
    }, [universitySettings]);

    useEffect(() => {
        if (universitySettings && defaultTemplateSettings) {
            const templateToUse = useCustomTemplate
                ? universitySettings.reportedSimBodyText
                : defaultTemplateSettings.reportedSimBodyText;

            // Set the raw template content - the ReportedSimEmailPreview component will handle all transformations
            setPreviewContent(templateToUse || '');
        }
    }, [
        useCustomTemplate,
        universitySettings,
        defaultTemplateSettings
    ]);

    const handleToggleClick = () => {
        setPendingValue(!useCustomTemplate);
        setIsDialogOpen(true);
    };

    const handleConfirm = async () => {
        setIsDialogOpen(false);

        try {
            const newUseDefaultTemplate = !pendingValue;

            let completeSettingsData = {
                ...universitySettings,
                tenancyId: tenancyData.tenancyId,
                useDefaultReportedSimTemplate: newUseDefaultTemplate
            };

            if (newUseDefaultTemplate) {
                // Switching TO default template - save the default values
                completeSettingsData = {
                    ...completeSettingsData,
                    reportedSimBodyText: defaultTemplateSettings?.reportedSimBodyText || "",
                    reportedSimMessage: defaultTemplateSettings?.reportedSimMessage || "",
                    reportedSimEmailSubject: defaultTemplateSettings?.reportedSimEmailSubject || ""
                };
            } else if (originalCustomTemplate) {
                // Switching FROM default template back to custom - restore original custom values
                completeSettingsData = {
                    ...completeSettingsData,
                    ...originalCustomTemplate
                };
            }

            await updateUniversityCourseSettings({
                tenancyId: tenancyData.tenancyId,
                settingsData: completeSettingsData
            }).unwrap();

            setUseCustomTemplate(pendingValue);

            // Set correct template content for preview
            const newTemplateContent = pendingValue
                ? (originalCustomTemplate?.reportedSimBodyText || universitySettings.reportedSimBodyText)
                : defaultTemplateSettings.reportedSimBodyText;

            setPreviewContent(newTemplateContent || '');

            setSnackbarMessage(
                pendingValue
                    ? "Custom reported sim email template enabled successfully"
                    : "Custom reported sim email template disabled successfully"
            );
            setSnackbarSeverity("success");
        } catch (error) {
            console.error("Error updating reported sim template settings:", error);
            setSnackbarMessage("Error updating reported sim template settings");
            setSnackbarSeverity("error");
        }

        setSnackbarOpen(true);
        setPendingValue(null);
    };

    const handleCancel = () => {
        setIsDialogOpen(false);
        setPendingValue(null);
    };

    const handlePreviewClick = () => {
        if (previewContent) {
            setIsPreviewModalOpen(true);
        }
    };

    const handleClosePreviewModal = () => {
        setIsPreviewModalOpen(false);
    };

    if (tenancyData) {
        return (
            <>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Grid container spacing={2} sx={{ mb: 4 }}>
                            <Grid container spacing={2} sx={{ mt: 2 }}>
                                <Grid item xs={12} md={6} lg={6}>
                                    <MainCard
                                        content={true}
                                        boxShadow={true}
                                        border={false}
                                        sx={{
                                            height: '100%',
                                            bgcolor: getCardColor(lightMode),
                                            display: 'flex',
                                            flexDirection: 'column'
                                        }}
                                    >
                                        <Box sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            width: "100%",
                                            mb: 1
                                        }}>
                                            <Box sx={{ display: "flex", alignItems: "center" }}>
                                                <Tooltip
                                                    title="Customise your reported simulation email templates or use the default template">
                                                    <IconButton>
                                                        <HelpIcon sx={{ color: "secondary.main" }} />
                                                    </IconButton>
                                                </Tooltip>
                                                <Typography variant="h4">
                                                    Email Customisation
                                                </Typography>
                                            </Box>
                                            <Tooltip
                                                title={!isCustomReportedSimEmailConfigured(universitySettings) ? "You must edit the default template and save it before enabling email customisation" : ""}>
                                                <span>
                                                    <LoadingSwitch
                                                        checked={useCustomTemplate}
                                                        onChange={handleToggleClick}
                                                        loading={universitySettingsLoading || defaultTemplateLoading}
                                                        disabled={!isCustomReportedSimEmailConfigured(universitySettings)}
                                                        inputProps={{ "aria-label": "reported sim template type toggle" }}
                                                    />
                                                </span>
                                            </Tooltip>
                                        </Box>
                                        <Divider sx={{ width: "100%", mb: 2 }} />

                                        {/* Use the ReportedSimEmailPreview component */}
                                        <ReportedSimEmailPreview
                                            content={previewContent}
                                            isLoading={universitySettingsLoading || defaultTemplateLoading}
                                            colors={emailColors}
                                            logoUrl={tenancyData?.logo ? `${APP_URL}/${tenancyData.logo}` : null}
                                            tenancyName={tenancyData?.name} 
                                            scale={0.6} // Smaller scale for the settings preview
                                        />

                                        <Box sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            gap: 2,
                                            marginTop: 1
                                        }}>
                                            <Button
                                                variant="contained"
                                                size="medium"
                                                onClick={handlePreviewClick}
                                                disabled={!previewContent || universitySettingsLoading || defaultTemplateLoading}
                                                sx={{ px: 3, width: "700px" }}
                                            >
                                                Preview
                                            </Button>
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                size="medium"
                                                onClick={() => navigate("/settings/reported-sim-email")}
                                                sx={{ px: 3, width: '700px' }}
                                            >
                                                Edit
                                            </Button>
                                        </Box>
                                    </MainCard>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>

                {/* Preview Modal */}
                <ReportedSimEmailPreviewModal
                    open={isPreviewModalOpen}
                    onClose={handleClosePreviewModal}
                    previewContent={previewContent}
                    tenancyData={tenancyData}
                    universitySettings={universitySettings}
                    colors={emailColors}
                />

                {/* Template Toggle Confirmation Dialog */}
                <Dialog
                    open={isDialogOpen}
                    onClose={handleCancel}
                    aria-labelledby="reported-sim-template-confirmation-dialog"
                >
                    <DialogTitle id="reported-sim-template-confirmation-dialog">
                        {pendingValue ? "Switch to Custom Reported Sim Template?" : "Switch to Default Reported Sim Template?"}
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            {pendingValue
                                ? "Switching to custom reported sim templates will replace any email settings currently in place. Are you sure you want to continue?"
                                : "Switching to the default reported sim template will disable your custom reported sim email templates. All users will receive the default template emails. Are you sure you want to continue?"
                            }
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCancel} color="primary">
                            Cancel
                        </Button>
                        <Button onClick={handleConfirm} color="primary" variant="contained">
                            Confirm
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Success/Error Snackbar */}
                <Snackbar
                    anchorOrigin={{ vertical: "top", horizontal: "center" }}
                    open={snackbarOpen}
                    autoHideDuration={3000}
                    onClose={() => setSnackbarOpen(false)}
                >
                    <Alert
                        onClose={() => setSnackbarOpen(false)}
                        severity={snackbarSeverity}
                        variant="filled"
                        sx={{ width: "100%" }}
                    >
                        {snackbarMessage}
                    </Alert>
                </Snackbar>
            </>
        );
    }
    return null;
}